<template>
    <el-dialog :title="title"
               :visible.sync="showDialog"
               @open="loadOrder"
               @close="closeDialog"
               width="60%"
               autoComplete="false"
               :close-on-click-modal="false">
        <el-form ref="form"
                 :model="form"
                 :rules="rules"
                 size="small"
                 label-width="220px"
                 label-position="left">
            <el-form-item :label="$t('orders.purchaseOrder')"
                          prop="purchaseOrderNumber">
                <el-input v-model="form.purchaseOrderNumber"
                          :disabled="disableForm"></el-input>
            </el-form-item>
            <el-form-item :label="$t('orders.orderNotes')"
                          prop="notes">
                <el-input type="textarea"
                          v-model="form.notes"
                          :disabled="disableForm"></el-input>
            </el-form-item>
            <el-form-item :label="$t('orders.orderDate')">
                <el-input v-model="form.placedAt"
                          disabled></el-input>
            </el-form-item>
            <el-form-item label-width="0">
                <h4 style="margin: 0">{{$t('orders.billingAddress')}}</h4>
                <ul style="margin: 0; padding: 0; list-style: none; line-height: 20px">
                    <li>
                        <b>{{$t('common.serviceCompanyName')}}</b> {{serviceCompany.name}}
                    </li>
                    <li>
                        <b>{{$t('common.address')}}</b> {{serviceCompany.address}}
                    </li>
                    <li>
                        <b>{{$t('common.phone')}}</b> {{serviceCompany.phone}}
                    </li>
                    <li>
                        <b>{{$t('common.code')}}</b> {{serviceCompany.code}}
                    </li>
                </ul>
            </el-form-item>
            <el-form-item :label="$t('orders.shippingAddressSameAsBillTo')"
                          class="shipping-method-toggle">
                <el-switch v-model="form.shippingAddressAsBillTo"
                           :disabled="disableForm"></el-switch>
            </el-form-item>
            <address-selector v-model="form"
                              :show-regional="false"
                              v-if="!form.shippingAddressAsBillTo"
                              :disable="disableForm">
                <el-form-item :label="$t('common.companyName')"
                              v-if="!form.shippingAddressAsBillTo">
                    <el-input v-model="form.shipToName"
                              :disabled="disableForm"></el-input>
                </el-form-item>
                <el-form-item :label="$t('common.phone')"
                              v-if="!form.shippingAddressAsBillTo">
                    <el-input v-model="form.shipToPhone"
                              :disabled="disableForm"></el-input>
                </el-form-item>
                <h4 slot="title">{{$t('orders.shippingAddress')}}</h4>
            </address-selector>
            <el-form-item :label="$t('common.phone')"
                          v-if="!form.shippingAddressAsBillTo">
                <el-input v-model="form.shipToPhone"
                          :disabled="disableForm"></el-input>
            </el-form-item>
            <el-form-item :label="$t('common.shippingMethod')"
                          prop="shippingMethod">
                <shipping-method-selector v-model="form.shippingMethod"
                                          :disabled="disableForm"></shipping-method-selector>
            </el-form-item>
            <el-form-item :label="$t('common.otherCompany')"
                          v-if="form.shippingMethod === '5'"
                          prop="otherCompany">
                <el-input v-model="form.otherCompany"
                          :disabled="disableForm"></el-input>
            </el-form-item>
            <h4>{{$t('common.parts')}}</h4>
            <el-form-item :label="$t('orders.addPart')">
                <part-selector @part-selected="partSelected"
                               :disabled="disableForm"
                               :serviceCompanyId="serviceCompany.id"
                               :title="$t('masters.toAddAnotherPartPleaseStartTypingNameOrPartNumber')"></part-selector>
            </el-form-item>
            <el-table :data="form.parts"
                      class="tablewithshading"
                      size="mini"
                      :summary-method="partsTotals"
                      show-summary>
                <el-table-column :label="$t('common.partNum')"
                                 width="100">
                    <template slot-scope="scope">
                        <el-tooltip effect="dark"
                                    :content="scope.row.name"
                                    placement="top">
                            <el-tag type="info"
                                    size="small"
                                    :disable-transitions="true"
                                    style="margin-right:5px;">{{scope.row.partNo}}</el-tag>
                        </el-tooltip>
                    </template>
                </el-table-column>
                <el-table-column :label="$t('common.partDescription')">
                    <template slot-scope="scope">
                        <small>{{$i18n.locale == 'en' ? scope.row.name : scope.row.nameFr}}</small>
                    </template>
                </el-table-column>
                <el-table-column prop="price"
                                 :label="$t('common.price')"
                                 width="100">
                    <template slot-scope="scope">
                        {{scope.row.price | money}}
                    </template>
                </el-table-column>
                <el-table-column prop="extendedPrice"
                                 :label="$t('common.extPrice')"
                                 width="100">
                    <template slot-scope="scope">
                        {{scope.row.extendedPrice | money}}
                    </template>
                </el-table-column>
                <el-table-column prop="quantity"
                                 :label="$t('common.quantity')"
                                 width="100">
                    <template slot-scope="scope">
                        <el-input-number size="mini"
                                         v-model="scope.row.quantity"
                                         :min="0"
                                         :max="100"
                                         style="width: 90px;"
                                         @change="quantityChanged($event, scope.row.id)"
                                         :disabled="orderId != null"
                                         v-if="$isUser"></el-input-number>
                        <span v-if="$isAdmin">{{scope.row.quantity}}</span>
                    </template>
                </el-table-column>
            </el-table>
            <div style="margin-top: 20px"
                 v-if="orderStatus == 1 || orderStatus == 2">
                <h4>Tracking Information</h4>
                <el-form-item :label="$t('common.trackingNumber')"
                              prop="trackingNumber">
                    <el-input v-model="form.trackingNumber"
                              :disabled="disableForm"></el-input>
                </el-form-item>
                <el-form-item :label="$t('orders.serviceOrderNumber')"
                              prop="serviceOrderNumber">
                    <el-input v-model="form.serviceOrderNumber"
                              :disabled="disableForm"></el-input>
                </el-form-item>
                <el-form-item :label="$t('orders.shippingCost')"
                              prop="shippingCharges">
                    <el-input type="number"
                              v-model.number="form.shippingCharges"
                              :disabled="disableForm"></el-input>
                </el-form-item>
                <el-form-item :label="$t('orders.signedBy')"
                              prop="deliverySignedBy">
                    <el-input v-model="form.deliverySignedBy"
                              :disabled="disableForm"></el-input>
                </el-form-item>
                <el-form-item :label="$t('orders.signedOn')"
                              prop="deliveryDate">
                    <el-date-picker v-model="form.deliveryDate"
                                    type="date"
                                    :placeholder="$t('common.selectDate')"
                                    :disabled="disableForm">
                    </el-date-picker>
                </el-form-item>
                <img src="static/ups_tracking.jpg"
                     @click="openTrackingWindow"
                     style="cursor: pointer; margin-left: auto; margin-right: auto; display: block;">
            </div>
        </el-form>
        <div v-if="orderStatus == 0 && $isFullAdmin" style="text-align: center;margin-top: 10px;">
            <el-button @click="exportParts"
                       type="primary">{{$t('common.export')}}</el-button>
        </div>
        <span slot="footer">
            <el-button :disabled="loading"
                       type="primary"
                       @click="printAndProcessOrder"
                       v-if="orderId != null && $isFullAdmin && orderStatus === 0">{{$t('orders.printAndProcess')}}</el-button>
            <el-button :disabled="loading"
                       type="primary"
                       @click="showSummary"
                       v-if="((orderId == null) || $isAdmin)">{{orderId == null ? $t('common.create') : $t('common.update')}}</el-button>
            <el-button :disabled="loading"
                       @click="closeDialog">{{$t('common.cancel')}}</el-button>
        </span>
        <el-dialog :title="title"
                   :visible.sync="previewSummaryVisible"
                   width="50%"
                   @close="closePreviewDialog"
                   append-to-body
                   :close-on-click-modal="false">
            <el-form size="small"
                     label-width="220px"
                     label-position="left">
                <el-form-item :label="$t('orders.purchaseOrder')">
                    <span>{{form.purchaseOrderNumber}}</span>
                </el-form-item>
                <el-form-item :label="$t('orders.orderNotes')">
                    <span>{{form.notes}}</span>
                </el-form-item>
                <el-form-item :label="$t('orders.orderDate')">
                    <span>{{form.placedAt}}</span>
                </el-form-item>
                <el-form-item label-width="0">
                    <h4 style="margin: 0">{{$t('orders.billingAddress')}}</h4>
                    <ul style="margin: 0; padding: 0; list-style: none; line-height: 20px">
                        <li>
                            <b>{{$t('common.serviceCompanyName')}}</b> {{serviceCompany.name}}
                        </li>
                        <li>
                            <b>{{$t('common.address')}}</b> {{serviceCompany.address}}
                        </li>
                        <li>
                            <b>{{$t('common.phone')}}</b> {{serviceCompany.phone}}
                        </li>
                        <li>
                            <b>{{$t('common.code')}}</b> {{serviceCompany.code}}
                        </li>
                    </ul>
                </el-form-item>
                <el-form-item :label="$t('orders.shippingAddressSameAsBillTo')"
                              class="shipping-method-toggle">
                    <span>{{form.shippingAddressAsBillTo ? $t('common.yes') : $t('common.no')}}</span>
                </el-form-item>
                <el-form-item label-width="0"
                              v-if="form.shippingAddressAsBillTo">
                    <h4 style="margin: 0">{{$t('orders.shippingAddress')}}</h4>
                    <ul style="margin: 0; padding: 0; list-style: none; line-height: 20px">
                        <li>
                            <b>{{$t('common.companyName')}}</b> {{serviceCompany.name}}
                        </li>
                        <li>
                            <b>{{$t('common.address')}}</b> {{serviceCompany.address}}
                        </li>
                        <li>
                            <b>{{$t('common.phone')}}</b> {{serviceCompany.phone}}
                        </li>
                        <li>
                            <b>{{$t('common.code')}}</b> {{serviceCompany.code}}
                        </li>
                    </ul>
                </el-form-item>
                <address-selector v-model="form"
                                  :show-regional="false"
                                  :showAsPreview="true"
                                  v-if="!form.shippingAddressAsBillTo">
                    <h4 slot="title">{{$t('orders.shippingAddress')}}</h4>
                </address-selector>
                <el-form-item :label="$t('common.companyName')"
                              v-if="!form.shippingAddressAsBillTo">
                    <span>{{form.shipToName}}</span>
                </el-form-item>
                <el-form-item :label="$t('common.phone')"
                              v-if="!form.shippingAddressAsBillTo">
                    <span>{{form.shipToPhone}}</span>
                </el-form-item>
                <el-form-item :label="$t('common.shippingMethod')">
                    {{
form.shippingMethod === '5' ? $t('common.otherCompany') :
          form.shippingMethod === '0' ? $t('common.express') :
          form.shippingMethod === '1' ? $t('common.expressSAT') :
          form.shippingMethod === '4' ? $t('common.standard') : $t('common.notSelected')
                    }}
                </el-form-item>
                <el-form-item :label="$t('common.otherCompany')"
                              v-if="form.shippingMethod === '5'">
                    <span>{{form.otherCompany}}</span>
                </el-form-item>
                <h4>{{$t('common.parts')}}</h4>
                <el-table :data="form.parts"
                          class="tablewithshading"
                          size="mini"
                          :summary-method="partsTotals"
                          show-summary>
                    <el-table-column :label="$t('common.partNum')">
                        <template slot-scope="scope">
                            <el-tooltip effect="dark"
                                        :content="scope.row.name"
                                        placement="top">
                                <el-tag type="info"
                                        size="small"
                                        :disable-transitions="true"
                                        style="margin-right:5px;">{{scope.row.partNo}}</el-tag>
                            </el-tooltip>
                        </template>
                    </el-table-column>
                    <el-table-column :label="$t('common.partDescription')">
                        <template slot-scope="scope">
                            <small>{{scope.row.name}}</small>
                        </template>
                    </el-table-column>
                    <el-table-column prop="price"
                                     :label="$t('common.price')">
                        <template slot-scope="scope">
                            {{scope.row.price | money}}
                        </template>
                    </el-table-column>
                    <el-table-column prop="extendedPrice"
                                     :label="$t('common.extPrice')">
                        <template slot-scope="scope">
                            {{scope.row.extendedPrice | money}}
                        </template>
                    </el-table-column>
                    <el-table-column prop="quantity"
                                     :label="$t('common.quantity')">
                        <template slot-scope="scope">
                            <span>{{scope.row.quantity}}</span>
                        </template>
                    </el-table-column>
                </el-table>
                <div style="margin-top: 15px"
                     v-if="orderStatus == 1">
                    <el-form-item :label="$t('common.trackingNumber')"
                                  prop="trackingNumber">
                        <span>{{form.trackingNumber}}</span>
                    </el-form-item>
                    <el-form-item :label="$t('orders.serviceOrderNumber')"
                                  prop="serviceOrderNumber">
                        <span>{{form.serviceOrderNumber}}</span>
                    </el-form-item>
                    <el-form-item :label="$t('orders.shippingCost')"
                                  prop="shippingCharges">
                        <span>{{form.shippingCharges}}</span>
                    </el-form-item>
                    <el-form-item :label="$t('orders.signedBy')"
                                  prop="deliverySignedBy">
                        <span>{{form.deliverySignedBy}}</span>
                    </el-form-item>
                    <el-form-item :label="$t('orders.signedOn')"
                                  prop="deliveryDate">
                        <span>{{form.deliveryDate | datetime}}</span>
                    </el-form-item>
                </div>
            </el-form>
            <span slot="footer">
                <el-button @click="save"
                           type="primary">{{$t('common.submit')}}</el-button>
                <el-button @click="closePreviewDialog">{{$t('common.back')}}</el-button>
            </span>
        </el-dialog>
    </el-dialog>
</template>

<script>
    import Vue from "vue";
    import api from "../api";
    import {
        AddressSelector,
        ShippingMethodSelector,
        PartSelector
    } from "@/app/shared/components";
    import { EventBus } from "@/app/event-bus.js";

    export default {
        name: "create-update-part-order",
        components: {
            AddressSelector,
            ShippingMethodSelector,
            PartSelector
        },
        props: {
            visible: {
                type: Boolean,
                default: false
            },
            orderId: {
                required: false,
                default: null
            }
        },
        data() {
            return {
                form: {
                    purchaseOrderNumber: "",
                    shippingAddressAsBillTo: true,
                    shippingMethod: undefined,
                    selectedCountryState: undefined,
                    selectedCountryStateName: "",
                    shipToName: "",
                    shipToPhone: "",
                    parts: [],
                    placedAt:
                        Number(new Date().getMonth() + 1 ) +
                        "/" +
                        new Date().getDate() +
                        "/" +
                        new Date().getFullYear(),
                    trackingNumber: "",
                    serviceOrderNumber: "",
                    shippingCharges: "",
                    deliverySignedBy: "",
                    deliveryDate: new Date()
                },
                rules: {
                    purchaseOrderNumber: [
                        {
                            required: true,
                            message: this.$t('common.pleaseEnterPurchaseOrderNumber'),
                            trigger: "blur"
                        }
                    ],
                    shippingMethod: [
                        {
                            required: true,
                            message: this.$t('shared.pleaseSelectShippingMethod'),
                            trigger: "change"
                        }
                    ],
                    otherCompany: [
                        {
                            validator: (rule, value, callback) => {
                                if (this.form.shippingMethod === "5" && !value) {
                                    // other company
                                    return callback(
                                        new Error(this.$t('common.pleaseEnterShippingCompanyName'))
                                    );
                                } else {
                                    return callback();
                                }
                            },
                            trigger: "blur"
                        }
                    ],
                    selectedCountryState: [
                        {
                            validator: (rule, value, callback) => {
                                if (this.form.shippingAddressAsBillTo || this.orderId != null) return callback();
                                if (value && Array.isArray(value) && value[0] && value[1])
                                    return callback();
                                else if (value && Array.isArray(value) && !value[1])
                                    return callback(new Error(this.$t('common.pleaseChooseState')));
                                else
                                    return callback(new Error(this.$t('common.pleaseChooseCountryAndState')));
                            },
                            trigger: "blur"
                        }
                    ],
                    address: [
                        {
                            validator: (rule, value, callback) => {
                                if (this.form.shippingAddressAsBillTo || this.orderId != null) return callback();
                                if (value) return callback();
                                else return callback(new Error(this.$t('common.pleaseEnterAddress')));
                            },
                            trigger: "blur"
                        }
                    ],
                    city: [
                        {
                            validator: (rule, value, callback) => {
                                if (this.form.shippingAddressAsBillTo || this.orderId != null) return callback();
                                if (value) return callback();
                                else return callback(new Error(this.$t('common.pleaseEnterCity')));
                            },
                            trigger: "blur"
                        }
                    ],
                    regional: [
                        {
                            validator: (rule, value, callback) => {
                                if (this.form.shippingAddressAsBillTo || this.orderId != null) return callback();
                                if (value) return callback();
                                else return callback(new Error(this.$t('common.pleaseEnterRegional')));
                            },
                            trigger: "blur"
                        }
                    ],
                    region: [
                        {
                            validator: (rule, value, callback) => {
                                if (this.form.shippingAddressAsBillTo || this.orderId != null) return callback();
                                if (value) return callback();
                                else return callback(new Error(this.$t('common.pleaseEnterRegion')));
                            },
                            trigger: "blur"
                        }
                    ],
                    zip: [
                        {
                            validator: (rule, value, callback) => {
                                if (this.form.shippingAddressAsBillTo || this.orderId != null) return callback();
                                if (value) return callback();
                                else return callback(new Error(this.$t('common.pleaseEnterZip')));
                            },
                            trigger: "blur"
                        }
                    ],
                    shipToPhone: [
                        {
                            validator: (rule, value, callback) => {
                                if (this.form.shippingAddressAsBillTo || this.orderId != null) return callback();
                                if (value) return callback();
                                else return callback(new Error(this.$t('common.pleaseEnterPhone')));
                            },
                            trigger: "blur"
                        }
                    ],
                    shipToName: [
                        {
                            validator: (rule, value, callback) => {
                                if (
                                    this.form.shippingAddressAsBillTo ||
                                    this.form.shippingMethod === "5" || this.orderId != null
                                )
                                    return callback();
                                if (value) return callback();
                                else return callback(new Error(this.$confirm(this.$t('common.pleaseEnterCompanyName'))))
                            },
                            trigger: "blur"
                        }
                    ]
                },
                serviceCompany: Object.assign({}, window.currentCompany),
                options: [],
                value: "",
                loading: false,
                showDialog: this.visible,
                previewSummaryVisible: false,
                orderStatus: null,
                mutatedOrderId: null,
                disableForm: false
            };
        },
        methods: {
            closeDialog() {
                this.showDialog = false;
                this.loading = false;
                this.form.address = null;
                this.form.selectedCountryState = null;
                this.form.city = null;
                this.form.regional = null;
                this.form.region = null;
                this.form.zip = null;
                this.form.parts = [];
                this.form.purchaseOrderNumber = null;
                this.form.shippingMethod = null;
                this.form.selectedCountryState = null;
                this.form.shipToName = null;
                this.form.shipToPhone = null;
                this.form.placedAt = null;
                this.form.notes = null;
                this.form.shippingAddressAsBillTo = true;
                if (this.previewSummaryVisible == true) {
                    this.previewSummaryVisible = false;
                }
                this.$emit("dialogClosed");
            },
            showSummary() {
                this.$refs["form"].validate(valid => {
                    if (valid) {
                        if (this.orderId != null) {
                            this.save();
                        } else {
                            this.previewSummaryVisible = true;
                        }
                    } else {
                        return false;
                    }
                });
            },
            save() {
                this.loading = true;
                api.saveOrder(this.orderId, this.form).then(
                    response => {
                        this.closeDialog();
                        if (!this.orderId) {
                            EventBus.$emit("order-placed");
                        }
                        if (this.form.status == 2) {	
                            this.$message({	
                                type: 'success',	
                                message: response.data	
                            });	
                        }
                    },
                    err => {
                        this.loading = false;
                        let message = "";
                        try {
                            message = Object.values(err.response.data).join(" ");
                        } catch (e) {
                            message = this.$t('common.cantCreateUpdateOrder');
                        }
                        this.$message.error(message);
                    }
                );
            },
            loadOrder() {
                if (this.orderId) {
                    this.loading = true;
                    api.getOrder(this.orderId).then(response => {
                        this.orderStatus = response.data.status;
                        this.serviceCompany = Object.assign({}, response.data.serviceCompany);
                        delete response.data.serviceCompany;
                        this.form = Object.assign({}, response.data);
                        this.form.shippingMethod = this.form.shippingMethod.toString();
                        this.form.placedAt = new Date(
                            this.form.placedAt
                        ).toLocaleDateString();
                        this.form.selectedCountryState = [
                            this.form.countryId,
                            this.form.state
                        ];
                        this.loading = false;
                    });
                } else {
                    this.loading = true;
                    api.getOrderId().then(response => {
                        debugger;
                        this.mutatedOrderId = response.data.data;
                        if (this.$isSubAgentUser)
                            this.serviceCompany = response.data.serviceCompany;
                        this.serviceCompany.code = response.data.serviceCompany.code;
                        this.form.placedAt = Number(new Date().getMonth() + 1) + "/" + new Date().getDate() + "/" + new Date().getFullYear();   
                        this.loading = false;
                    });
                }
                this.disableForm = this.orderId != null && this.$isUser;
            },
            partsTotals(param) {
                const { columns, data } = param;
                const sums = [];
                columns.forEach((column, index) => {
                    if (index === 0) {
                        sums[index] = "Total";
                        return;
                    }
                    if (index === 4 || index === 1) {
                        sums[index] = "N/A";
                        return;
                    }
                    const values = data.map(
                        item => Number(item[column.property]) * Number(item["quantity"])
                    );
                    if (!values.every(value => isNaN(value))) {
                        sums[index] =
                            "$" +
                            values
                                .reduce((prev, curr) => {
                                    const value = Number(curr);
                                    if (!isNaN(value)) {
                                        return prev + curr;
                                    } else {
                                        return prev;
                                    }
                                }, 0)
                                .toFixed(2);
                    }
                });
                return sums;
            },
            quantityChanged(val, id) {
                if (val === 0) {
                    setTimeout(() => {
                        this.form.parts = this.form.parts.filter(p => p.id !== id);
                    });
                }
            },
            partSelected(addedPart) {
                var part = this.form.parts.find(p => p.id === addedPart.id);
                if (part === undefined) {
                    addedPart.quantity = 1;
                    this.form.parts.push(addedPart);
                }
            },
            closePreviewDialog() {
                this.previewSummaryVisible = false;
            },
            printAndProcessOrder() {
                var self = this;
                api.printAndProcessOrder(this.orderId).then(response => {
                    var oHiddFrame = document.createElement("iframe");
                    oHiddFrame.style.visibility = "hidden";
                    oHiddFrame.style.position = "fixed";
                    oHiddFrame.style.right = "0";
                    oHiddFrame.style.bottom = "0";
                    oHiddFrame.src = "javascript:void(0)";
                    document.body.appendChild(oHiddFrame);
                    var dstDoc =
                        oHiddFrame.contentDocument || oHiddFrame.contentWindow.document;
                    dstDoc.write(response.data);
                    var isFirefox = typeof InstallTrigger !== "undefined";
                    if (isFirefox) {
                        oHiddFrame.contentWindow.print();
                        document.body.removeChild(oHiddFrame);
                        self.$message({
                            showClose: true,
                            message: this.$t('common.partOrderProcessedSuccesfully'),
                            type: "success"
                        });
                        this.closeDialog();
                    } else {
                        setTimeout(function () {
                            oHiddFrame.contentWindow.print();
                            document.body.removeChild(oHiddFrame);
                            self.$message({
                                showClose: true,
                                message: this.$t('common.partOrderProcessedSuccesfully'),
                                type: "success"
                            });
                        }, 500);
                        this.closeDialog();
                    }
                });
            },
            openTrackingWindow() {
                var url = "http://www.ups.com/WebTracking/track?loc=en_US&WT.svl=PNRO_L1";
                if (this.form.trackingNumber) {
                    url = url + "&trackNums=" + this.form.trackingNumber;
                }
                window.open(url);
            },
            exportParts() {
                this.loading = true;
                api
                    .exportOrderParts(this.orderId)
                    .then(() => {
                        this.loading = false;
                    })
                    .catch(() => {
                        this.loading = false;
                    });
            }
        },
        watch: {
            visible: function (val) {
                this.showDialog = val;
            }
        },
        computed: {
            title: function () {
                return this.$isFullAdmin
                    ? "Update Part Order #" + this.orderId
                    : this.orderId == null && this.mutatedOrderId != null
                        ? "Place New Part Order #" + this.mutatedOrderId
                        : "View Part Order #" + this.orderId;
            }
        }
    };
</script>


// WEBPACK FOOTER //
// ClientApp/app/orders/components/CreateUpdatePartOrderDialog.vue